const ONESIGNAL_APP_ID = '8e00793b-8f34-4b18-be7e-3aa01118fbd3';
const ONESIGNAL_SDK_URL = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
const STORED_APP_ID_KEY = 'rv_onesignal_app_id';
const INIT_TIMEOUT_MS = 45000;

let initPromise = null;
let initSucceeded = false;
let initAttempt = 0;
let sdkScriptPromise = null;
let oneSignalSdk = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withTimeout = (promise, ms, label) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`${label} timeout`)), ms);
    }),
  ]);

const isAlreadyInitializedError = (error) => {
  const message = error?.message || String(error);
  return (
    message.includes('already initialized') ||
    message.includes('SDK already initialized')
  );
};

const isAppIdMismatchError = (error) => {
  const message = error?.message || String(error);
  return (
    message.includes("AppID doesn't match") ||
    message.includes('does not match any existing app') ||
    message.includes('does not match existing')
  );
};

export const getOneSignalSdk = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return oneSignalSdk ?? window.OneSignal ?? null;
};

const ensureOneSignalScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('OneSignal solo está disponible en el navegador'));
  }

  if (sdkScriptPromise) {
    return sdkScriptPromise;
  }

  sdkScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${ONESIGNAL_SDK_URL}"]`);

    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = ONESIGNAL_SDK_URL;
    script.async = true;
    script.defer = true;

    script.addEventListener('load', () => {
      resolve();
    });
    script.addEventListener('error', () => {
      reject(new Error('No se pudo cargar el SDK de OneSignal'));
    });

    document.head.appendChild(script);
  }).catch((error) => {
    sdkScriptPromise = null;
    throw error;
  });

  return sdkScriptPromise;
};

const runWithOneSignal = (callback, timeoutMs = INIT_TIMEOUT_MS, label = 'OneSignal') => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('OneSignal solo está disponible en el navegador'));
  }

  window.OneSignalDeferred = window.OneSignalDeferred || [];

  return withTimeout(
    new Promise((resolve, reject) => {
      const run = async (sdk) => {
        try {
          oneSignalSdk = sdk;
          resolve(await callback(sdk));
        } catch (error) {
          reject(error);
        }
      };

      const currentSdk = getOneSignalSdk();
      if (currentSdk?.init || currentSdk?.User) {
        run(currentSdk);
        return;
      }

      window.OneSignalDeferred.push(run);
      ensureOneSignalScript().catch(reject);
    }),
    timeoutMs,
    label
  );
};

export const clearOneSignalBrowserState = async () => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (/onesignal/i.test(key)) {
        localStorage.removeItem(key);
      }
    });

    Object.keys(sessionStorage).forEach((key) => {
      if (/onesignal/i.test(key)) {
        sessionStorage.removeItem(key);
      }
    });

    if (window.indexedDB?.databases) {
      const databases = await window.indexedDB.databases();
      await Promise.all(
        databases
          .filter((db) => db.name && /onesignal/i.test(db.name))
          .map(
            (db) =>
              new Promise((resolve) => {
                const request = window.indexedDB.deleteDatabase(db.name);
                request.onsuccess = () => resolve();
                request.onerror = () => resolve();
                request.onblocked = () => resolve();
              })
          )
      );
    }

    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations
          .filter((registration) => {
            const scriptUrl =
              registration.active?.scriptURL ||
              registration.installing?.scriptURL ||
              registration.waiting?.scriptURL ||
              '';
            return /OneSignalSDK/i.test(scriptUrl);
          })
          .map((registration) => registration.unregister())
      );
    }
  } catch (error) {
    console.warn('No se pudo limpiar el estado de OneSignal:', error);
  }

  document.querySelectorAll(`script[src="${ONESIGNAL_SDK_URL}"]`).forEach((script) => {
    script.remove();
  });

  delete window.OneSignal;
  window.OneSignalDeferred = [];
  oneSignalSdk = null;
  initSucceeded = false;
  sdkScriptPromise = null;
};

const runInit = (options = {}) => {
  const { autoPrompt = false } = options;

  return runWithOneSignal(async (sdk) => {
    try {
      await sdk.init({
        appId: ONESIGNAL_APP_ID,
        allowLocalhostAsSecureOrigin: true,
        serviceWorkerPath: '/OneSignalSDKWorker.js',
        serviceWorkerUpdaterPath: '/OneSignalSDKUpdaterWorker.js',
        serviceWorkerParam: { scope: '/' },
      });

      localStorage.setItem(STORED_APP_ID_KEY, ONESIGNAL_APP_ID);
      initSucceeded = true;

      if (autoPrompt) {
        sdk.Slidedown?.promptPush?.();
      }

      return sdk;
    } catch (error) {
      if (isAlreadyInitializedError(error)) {
        initSucceeded = true;
        localStorage.setItem(STORED_APP_ID_KEY, ONESIGNAL_APP_ID);
        return sdk;
      }

      if (isAppIdMismatchError(error) && initAttempt < 1) {
        initAttempt += 1;
        await clearOneSignalBrowserState();
        initPromise = null;
        return runInit(options);
      }

      initPromise = null;
      initSucceeded = false;
      throw error;
    }
  }, INIT_TIMEOUT_MS, 'OneSignal init');
};

export const isOneSignalReady = () => initSucceeded;

export const resetOneSignalInit = () => {
  initPromise = null;
  initSucceeded = false;
  oneSignalSdk = null;
  sdkScriptPromise = null;
  document.querySelectorAll(`script[src="${ONESIGNAL_SDK_URL}"]`).forEach((script) => {
    script.remove();
  });
  window.OneSignalDeferred = [];
};

export const getPushSubscriptionInfo = () => {
  try {
    const sub = getOneSignalSdk().User?.PushSubscription;
    return {
      id: sub?.id ?? null,
      token: sub?.token ?? null,
      optedIn: Boolean(sub?.optedIn),
    };
  } catch {
    return { id: null, token: null, optedIn: false };
  }
};

export const hasActivePushSubscription = () => {
  const { id, token, optedIn } = getPushSubscriptionInfo();
  return optedIn && Boolean(id || token);
};

export const waitForPushSubscription = async (timeoutMs = 20000) => {
  const read = () => getPushSubscriptionInfo();

  let current = read();
  if (current.id || current.token) {
    return current;
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let pollId = null;

    const finish = (result, asError = false) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      if (pollId) clearInterval(pollId);
      try {
        getOneSignalSdk().User?.PushSubscription?.removeEventListener?.('change', onChange);
      } catch {
        // ignore
      }
      if (asError) reject(result);
      else resolve(result);
    };

    const onChange = () => {
      const next = read();
      if (next.id || next.token) {
        finish(next);
      }
    };

    const timeoutId = setTimeout(() => {
      const final = read();
      if (final.id || final.token) {
        finish(final);
      } else {
        finish(new Error('SUBSCRIPTION_TIMEOUT'), true);
      }
    }, timeoutMs);

    try {
      getOneSignalSdk().User?.PushSubscription?.addEventListener?.('change', onChange);
    } catch {
      // ignore
    }

    pollId = setInterval(() => {
      const next = read();
      if (next.id || next.token) {
        finish(next);
      }
    }, 400);
  });
};

export const waitForOneSignalReady = async () => {
  const storedAppId = localStorage.getItem(STORED_APP_ID_KEY);
  if (storedAppId && storedAppId !== ONESIGNAL_APP_ID) {
    await clearOneSignalBrowserState();
    initPromise = null;
  }

  try {
    await (initPromise ?? initializeOneSignal());
  } catch (error) {
    console.warn('OneSignal no listo:', error);
    return false;
  }

  for (let attempt = 0; attempt < 50; attempt += 1) {
    if (initSucceeded && getOneSignalSdk()?.Notifications) {
      return true;
    }
    await wait(100);
  }

  return initSucceeded;
};

export const getOneSignalExternalId = () => {
  try {
    return getOneSignalSdk().User?.externalId ?? null;
  } catch {
    return null;
  }
};

export const waitForOneSignalExternalId = async (externalId, timeoutMs = 8000) => {
  const expected = String(externalId);
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    if (getOneSignalExternalId() === expected) {
      return true;
    }
    await wait(250);
  }

  return getOneSignalExternalId() === expected;
};

export const ensureOneSignalLogin = async (
  userId,
  { requirePermission = false, throwOnFailure = false } = {}
) => {
  if (!userId) return;

  const permission = window.Notification?.permission;
  if (requirePermission && permission !== 'granted') {
    return;
  }

  const ready = await waitForOneSignalReady();
  if (!ready || !initSucceeded) {
    if (throwOnFailure) {
      throw new Error('OneSignal no está listo para vincular tu cuenta.');
    }
    return;
  }

  const externalId = String(userId);

  if (getOneSignalExternalId() === externalId) {
    return;
  }

  await wait(200);

  try {
    await runWithOneSignal(
      async (sdk) => {
        await sdk.login(externalId);
      },
      12000,
      'OneSignal login'
    );
    await waitForOneSignalExternalId(externalId);
  } catch (error) {
    console.warn('OneSignal.login no completado:', error);
    if (throwOnFailure) {
      throw error;
    }
  }
};

export const logoutOneSignal = async () => {
  const sdk = getOneSignalSdk();
  if (!sdk?.logout) return;

  try {
    await sdk.logout();
  } catch (error) {
    console.warn('OneSignal.logout no completado:', error);
  }
};

export const initializeOneSignal = (options = {}) => {
  if (initPromise) return initPromise;

  const storedAppId = localStorage.getItem(STORED_APP_ID_KEY);
  if (storedAppId && storedAppId !== ONESIGNAL_APP_ID) {
    initPromise = clearOneSignalBrowserState()
      .then(() => {
        initPromise = null;
        return initializeOneSignal(options);
      })
      .catch(() => {
        initPromise = null;
      });
    return initPromise;
  }

  initPromise = withTimeout(runInit(options), INIT_TIMEOUT_MS, 'OneSignal init').catch(
    (error) => {
      initPromise = null;
      initSucceeded = false;
      console.warn('Error inicializando OneSignal:', error);
      throw error;
    }
  );

  return initPromise;
};
